import React, { useState, useRef, useEffect } from 'react';
import { useProfile, type TwoFactorSetup } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import Avatar from '@/components/ui/Avatar';
import CasinoBackground from '@/components/ui/CasinoBackground';

const NOTIFICATION_TYPE_STRINGS: string[] = [
  'friends',
  'games',
  'system',
]

const NOTIFICATION_TYPE_DESCRIPTIONS: string[] = [
  'Friend requests and acceptances.',
  'Game invites, your turn reminders, and match results.',
  'Deposit, Withdraw and Account Updates.',
]

const NOTIFICATION_TYPE_ICONS: React.ReactNode[] = [
  (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75}>
      <circle cx="9" cy="8" r="3" />
      <circle cx="16.5" cy="9.5" r="2.25" />
      <path strokeLinecap="round" d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path strokeLinecap="round" d="M14.75 14.75c2.25.25 3.75 2 3.75 4.25" />
    </svg>
  ),
  (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75}>
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <circle cx="9" cy="9" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="9" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="15" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="15" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 8a6 6 0 1112 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6Z" />
      <path strokeLinecap="round" d="M10 20a2 2 0 004 0" />
    </svg>
  ),
]

const NotificationTypeToggle: React.FC<{
  title:       string,
  description: string,
  icon:        React.ReactNode,
  checked:     boolean,
  setter:      (value: boolean) => void,
}> = ({
  title, description, icon, checked, setter
}) => {
  return (
    <label className="flex items-center gap-4 py-4 first:pt-0 last:pb-0 cursor-pointer">
      <span className={
        'flex items-center justify-center w-10 h-10 shrink-0 rounded-xl border transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] '
        + (checked
          ? 'bg-[rgba(212,175,55,0.12)] border-[rgba(212,175,55,0.35)] text-[var(--gold)]'
          : 'bg-[var(--surface-2)] border-[rgba(212,175,55,0.1)] text-[var(--text-3)]')
      }>
        {icon}
      </span>
      <span className="min-w-0 flex-1 select-none">
        <span className="block text-sm font-semibold text-[var(--text)] capitalize">{title}</span>
        <span className="block text-xs text-[var(--text-3)] mt-0.5 leading-snug">{description}</span>
      </span>
      <input
        type="checkbox"
        value=""
        className="sr-only peer"
        checked={checked}
        onChange={(e) => {setter(e.target.checked)}}
      />
      <div className="
        relative
        shrink-0
        w-11
        h-6
        rounded-full
        bg-[var(--surface-2)]
        border
        border-[rgba(212,175,55,0.15)]
        transition-colors
        duration-200
        ease-[cubic-bezier(0.23,1,0.32,1)]
        peer-checked:bg-[var(--gold)]
        peer-checked:border-[var(--gold)]
        peer-focus-visible:ring-2
        peer-focus-visible:ring-[rgba(212,175,55,0.35)]
        active:scale-95
        after:content-['']
        after:absolute after:top-[2px]
        after:start-[2px]
        after:bg-white
        after:shadow-[0_1px_2px_rgba(0,0,0,0.35)]
        after:rounded-full
        after:h-[1.125rem]
        after:w-[1.125rem]
        after:transition-transform
        after:duration-200
        after:ease-[cubic-bezier(0.23,1,0.32,1)]
        peer-checked:after:translate-x-5
      "></div>
    </label>
  );
}

const ProfilePage: React.FC = () => {
  const {
    user,
    notificationTypes,
    updateProfile,
    uploadAvatar,
    profile_setNotificationTypes,
    setupTwoFactor,
    confirmTwoFactor,
    disableTwoFactor,
  } = useProfile();
  const { refreshUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [identitySuccess, setIdentitySuccess] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [identityLoading, setIdentityLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [twoFactorStep, setTwoFactorStep] = useState<'idle' | 'setup' | 'backup-codes' | 'disable'>('idle');
  const [twoFactorSetupData, setTwoFactorSetupData] = useState<TwoFactorSetup | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [disablePassword, setDisablePassword] = useState('');

  const [haveNotificationTypes, setHaveNotificationTypes] = useState<boolean[]>(
    new Array(NOTIFICATION_TYPE_STRINGS.length).fill(true)
  );
  const [savedHaveNotificationTypes, setSavedHaveNotificationTypes] = useState<boolean[]>(
    new Array(NOTIFICATION_TYPE_STRINGS.length).fill(true)
  );
  const notificationSettingsChanged = haveNotificationTypes.some(
    (haveNotificationType, index) => haveNotificationType !== savedHaveNotificationTypes[index]
  );
  const setHaveNotificationTypesIndex = (index: number) => {
    return (value: boolean) => {
      var nextHaveNotificationTypes: boolean[] = structuredClone(haveNotificationTypes);
      nextHaveNotificationTypes[index] = value;
      setHaveNotificationTypes(nextHaveNotificationTypes);
    };
  };

  const [notificationSettingsSuccess,  setNotificationSettingsSuccess]  = useState(false);
  const [notificationSettingsError,    setNotificationSettingsError]    = useState<string | null>(null);
  const [notificationSettingsApplying, setNotificationSettingsApplying] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username);
      setEmail(user.email);
    }
    if (notificationTypes) {
      var nextHaveNotificationTypes: boolean[] = Array(NOTIFICATION_TYPE_STRINGS.length).fill(false);
      notificationTypes.forEach((notificationType) => {
        var index: number = NOTIFICATION_TYPE_STRINGS.indexOf(notificationType);
        if(index == -1) {
          console.error("Invalid notification type: " + notificationType);
        } else {
          nextHaveNotificationTypes[index] = true;
        }
      })
      setHaveNotificationTypes(nextHaveNotificationTypes);
      setSavedHaveNotificationTypes(nextHaveNotificationTypes);
    }
  }, [user?.id, notificationTypes]);

  const USERNAME_PATTERN = /^[a-zA-Z0-9_.-]+$/;
  const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleIdentitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIdentitySuccess(false);
    setIdentityError(null);

    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim();
    if (!trimmedUsername || !trimmedEmail) {
      setIdentityError('Username and email are required');
      return;
    }
    if (trimmedUsername.length < 3 || trimmedUsername.length > 32 || !USERNAME_PATTERN.test(trimmedUsername)) {
      setIdentityError('Username must be 3-32 characters and contain only letters, numbers, underscores, hyphens, and dots');
      return;
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setIdentityError('Please enter a valid email address');
      return;
    }

    setIdentityLoading(true);
    try {
      await updateProfile(trimmedUsername, trimmedEmail);
      await refreshUser();
      setIdentitySuccess(true);
      setTimeout(() => setIdentitySuccess(false), 3000);
    } catch (err) {
      setIdentityError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setIdentityLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSuccess(false);
    setPasswordError(null);
    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await updateProfile(user?.username ?? '', user?.email ?? '', newPassword, currentPassword);
      await refreshUser();
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setPasswordLoading(false);
    }
  };

  const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5MB, matches backend limit
  const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setAvatarError('Unsupported file type. Use JPEG, PNG, GIF, or WebP.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError('Image is too large. Max size is 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setAvatarUploading(true);
    try {
      await uploadAvatar(file);
      await refreshUser();
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleStartTwoFactorSetup = async () => {
    setTwoFactorError(null);
    setTwoFactorLoading(true);
    try {
      const data = await setupTwoFactor();
      setTwoFactorSetupData(data);
      setTwoFactorStep('setup');
    } catch (err) {
      setTwoFactorError(err instanceof Error ? err.message : 'Failed to start setup');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const TOTP_CODE_PATTERN = /^\d{6}$/;

  const handleConfirmTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorError(null);
    const trimmedCode = twoFactorCode.trim();
    if (!TOTP_CODE_PATTERN.test(trimmedCode)) {
      setTwoFactorError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setTwoFactorLoading(true);
    try {
      const codes = await confirmTwoFactor(trimmedCode);
      setBackupCodes(codes);
      setTwoFactorStep('backup-codes');
      setTwoFactorCode('');
    } catch (err) {
      setTwoFactorError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleFinishTwoFactorSetup = () => {
    setTwoFactorStep('idle');
    setTwoFactorSetupData(null);
    setBackupCodes([]);
  };

  const handleCancelTwoFactorSetup = () => {
    setTwoFactorStep('idle');
    setTwoFactorSetupData(null);
    setTwoFactorCode('');
    setTwoFactorError(null);
  };

  const handleDisableTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorError(null);
    setTwoFactorLoading(true);
    try {
      await disableTwoFactor(disablePassword);
      setDisablePassword('');
      setTwoFactorStep('idle');
    } catch (err) {
      setTwoFactorError(err instanceof Error ? err.message : 'Failed to disable two-factor authentication');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleApplyNotificationSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotificationSettingsSuccess(false);
    setNotificationSettingsError(null);
    setNotificationSettingsApplying(true);
    try {
      var notificationTypesToSend: string[] = [];
      haveNotificationTypes.forEach((haveNotificationType, notificationTypeIndex) => {
        if(haveNotificationType) {
          notificationTypesToSend.push(NOTIFICATION_TYPE_STRINGS[notificationTypeIndex])
        }
      })
      await profile_setNotificationTypes(notificationTypesToSend);
      setSavedHaveNotificationTypes(haveNotificationTypes);
      setNotificationSettingsSuccess(true);
      setTimeout(() => setNotificationSettingsSuccess(false), 3000);
    } catch(err) {
      setNotificationSettingsError(err instanceof Error ? err.message : 'Apply failed');
    } finally {
      setNotificationSettingsApplying(false);
    }
  }

  const inputClass =
    'w-full px-4 py-3 rounded-lg bg-[var(--surface-2)] border border-[rgba(212,175,55,0.15)] text-[var(--text)] placeholder-[var(--text-3)] focus:outline-none focus:border-[rgba(212,175,55,0.4)] focus:ring-2 focus:ring-[rgba(212,175,55,0.1)] input-focus-transition';
  const labelClass = 'block text-xs uppercase tracking-widest font-semibold text-[var(--text-3)] mb-2';
  const saveButtonClass =
    'px-6 py-2.5 rounded-lg bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.3)] text-[var(--gold)] font-semibold text-sm uppercase tracking-wider hover:bg-[rgba(212,175,55,0.18)] hover:border-[rgba(212,175,55,0.5)] active:scale-[0.99] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <main className="overflow-x-hidden w-full max-w-full">
      <div className="min-h-screen bg-[var(--base)] text-[var(--text)] pt-24 pb-16 px-6">
        <CasinoBackground />
        <div className="relative z-10 max-w-5xl mx-auto fade-in-up">
          <div className="mb-8">
            <p className="eyebrow mb-2">Settings</p>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold leading-tight tracking-tight">
              Your <span className="text-[var(--gold)] italic">Profile</span>
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8">
            <div className="flex flex-col items-center gap-3">
              <Avatar
                avatarURL={user?.avatarURL}
                size={96}
                onClick={() => fileInputRef.current?.click()}
                isUploading={avatarUploading}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
              <p className="text-xs text-[var(--text-3)] text-center">Click to change</p>
              {avatarError && <p className="text-xs text-red-400 text-center max-w-[120px]">{avatarError}</p>}
              {user && (
                <div className="text-center mt-1">
                  <p className="font-serif text-lg font-semibold text-[var(--text)]">{user.username}</p>
                  <p className="text-xs text-[var(--text-3)] mt-0.5">
                    Joined {new Date(user.joined_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-[rgba(212,175,55,0.12)] bg-[var(--surface)] p-6">
                <h2 className="font-serif text-xl font-semibold text-[var(--text)] mb-5">Identity</h2>
                <form onSubmit={handleIdentitySubmit} className="space-y-4">
                  <div>
                    <label htmlFor="username" className={labelClass}>Username</label>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Username"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className={labelClass}>Email</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                      className={inputClass}
                    />
                  </div>
                  {identityError && <p className="text-sm text-red-400">{identityError}</p>}
                  {identitySuccess && <p className="text-sm text-emerald-400">Changes saved</p>}
                  <div className="flex justify-end">
                    <button type="submit" disabled={identityLoading} className={saveButtonClass}>
                      {identityLoading ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="rounded-2xl border border-[rgba(212,175,55,0.12)] bg-[var(--surface)] p-6">
                <h2 className="font-serif text-xl font-semibold text-[var(--text)] mb-5">Change Password</h2>
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="current-password" className={labelClass}>Current password</label>
                    <input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Your current password"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="new-password" className={labelClass}>New password</label>
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="confirm-password" className={labelClass}>Confirm password</label>
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      className={inputClass}
                    />
                  </div>
                  {passwordError && <p className="text-sm text-red-400">{passwordError}</p>}
                  {passwordSuccess && <p className="text-sm text-emerald-400">Password updated</p>}
                  <div className="flex justify-end">
                    <button type="submit" disabled={passwordLoading || !newPassword || !currentPassword} className={saveButtonClass}>
                      {passwordLoading ? 'Updating…' : 'Update password'}
                    </button>
                  </div>
                </form>
              </div>

              <div className="rounded-2xl border border-[rgba(212,175,55,0.12)] bg-[var(--surface)] p-6">
                <h2 className="font-serif text-xl font-semibold text-[var(--text)] mb-1">Two-Factor Authentication</h2>
                <p className="text-sm text-[var(--text-3)] mb-5">
                  Require a code from an authenticator app when signing in.
                </p>

                {twoFactorStep === 'idle' && (
                  <>
                    {user?.two_factor_enabled ? (
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm text-emerald-400">Two-factor authentication is enabled.</p>
                        <button
                          type="button"
                          onClick={() => setTwoFactorStep('disable')}
                          className="px-6 py-2.5 rounded-lg bg-[rgba(220,38,38,0.1)] border border-[rgba(220,38,38,0.3)] text-red-400 font-semibold text-sm uppercase tracking-wider hover:bg-[rgba(220,38,38,0.18)] hover:border-[rgba(220,38,38,0.5)] active:scale-[0.99] transition-all duration-200"
                        >
                          Disable
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm text-[var(--text-2)]">Two-factor authentication is disabled.</p>
                        <button
                          type="button"
                          onClick={handleStartTwoFactorSetup}
                          disabled={twoFactorLoading}
                          className={saveButtonClass}
                        >
                          {twoFactorLoading ? 'Starting…' : 'Enable'}
                        </button>
                      </div>
                    )}
                    {twoFactorError && <p className="text-sm text-red-400 mt-4">{twoFactorError}</p>}
                  </>
                )}

                {twoFactorStep === 'setup' && twoFactorSetupData && (
                  <div className="space-y-4">
                    <p className="text-sm text-[var(--text-2)]">
                      Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy).
                    </p>
                    <div className="flex justify-center">
                      <img
                        src={twoFactorSetupData.qr_code}
                        alt="Two-factor authentication QR code"
                        className="w-48 h-48 rounded-lg border border-[rgba(212,175,55,0.15)] bg-white p-2"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Can't scan? Enter this code manually</label>
                      <code className="block w-full px-4 py-3 rounded-lg bg-[var(--surface-2)] border border-[rgba(212,175,55,0.15)] text-[var(--text)] text-sm break-all">
                        {twoFactorSetupData.secret}
                      </code>
                    </div>
                    <form onSubmit={handleConfirmTwoFactor} className="space-y-4">
                      <div>
                        <label htmlFor="two-factor-code" className={labelClass}>Enter the 6-digit code to confirm</label>
                        <input
                          id="two-factor-code"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={twoFactorCode}
                          onChange={(e) => {
                            setTwoFactorCode(e.target.value);
                            if (twoFactorError) setTwoFactorError(null);
                          }}
                          placeholder="123456"
                          className={`${inputClass} tracking-widest`}
                        />
                      </div>
                      {twoFactorError && <p className="text-sm text-red-400">{twoFactorError}</p>}
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleCancelTwoFactorSetup}
                          className="px-6 py-2.5 rounded-lg text-sm font-semibold text-[var(--text-2)] hover:text-[var(--text)] transition-colors"
                        >
                          Cancel
                        </button>
                        <button type="submit" disabled={twoFactorLoading || !twoFactorCode} className={saveButtonClass}>
                          {twoFactorLoading ? 'Confirming…' : 'Confirm & enable'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {twoFactorStep === 'backup-codes' && (
                  <div className="space-y-4">
                    <p className="text-sm text-emerald-400">Two-factor authentication is now enabled.</p>
                    <p className="text-sm text-[var(--text-2)]">
                      Save these backup codes somewhere safe. Each one can be used once to sign in if you lose
                      access to your authenticator app. They won't be shown again.
                    </p>
                    <div className="grid grid-cols-2 gap-2 px-4 py-3 rounded-lg bg-[var(--surface-2)] border border-[rgba(212,175,55,0.15)]">
                      {backupCodes.map((code) => (
                        <code key={code} className="text-sm text-[var(--text)] tracking-wider">{code}</code>
                      ))}
                    </div>
                    <div className="flex justify-end">
                      <button type="button" onClick={handleFinishTwoFactorSetup} className={saveButtonClass}>
                        I've saved my backup codes
                      </button>
                    </div>
                  </div>
                )}

                {twoFactorStep === 'disable' && (
                  <form onSubmit={handleDisableTwoFactor} className="space-y-4">
                    <div>
                      <label htmlFor="disable-password" className={labelClass}>Confirm your password to disable</label>
                      <input
                        id="disable-password"
                        type="password"
                        value={disablePassword}
                        onChange={(e) => {
                          setDisablePassword(e.target.value);
                          if (twoFactorError) setTwoFactorError(null);
                        }}
                        placeholder="Current password"
                        className={inputClass}
                      />
                    </div>
                    {twoFactorError && <p className="text-sm text-red-400">{twoFactorError}</p>}
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => { setTwoFactorStep('idle'); setDisablePassword(''); setTwoFactorError(null); }}
                        className="px-6 py-2.5 rounded-lg text-sm font-semibold text-[var(--text-2)] hover:text-[var(--text)] transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={twoFactorLoading || !disablePassword}
                        className="px-6 py-2.5 rounded-lg bg-[rgba(220,38,38,0.1)] border border-[rgba(220,38,38,0.3)] text-red-400 font-semibold text-sm uppercase tracking-wider hover:bg-[rgba(220,38,38,0.18)] hover:border-[rgba(220,38,38,0.5)] active:scale-[0.99] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {twoFactorLoading ? 'Disabling…' : 'Disable'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              <div className="rounded-2xl border border-[rgba(212,175,55,0.12)] bg-[var(--surface)] p-6">
                <h2 className="font-serif text-xl font-semibold text-[var(--text)]">Notification Settings</h2>
                <p className="text-sm text-[var(--text-3)] mt-1 mb-2">Choose what you want to hear about.</p>
                <form onSubmit={handleApplyNotificationSettings}>
                  <div className="divide-y divide-[rgba(212,175,55,0.08)]">
                    <NotificationTypeToggle
                      title={NOTIFICATION_TYPE_STRINGS[0]}
                      description={NOTIFICATION_TYPE_DESCRIPTIONS[0]}
                      icon={NOTIFICATION_TYPE_ICONS[0]}
                      checked={haveNotificationTypes[0]}
                      setter={setHaveNotificationTypesIndex(0)}
                    />
                    <NotificationTypeToggle
                      title={NOTIFICATION_TYPE_STRINGS[1]}
                      description={NOTIFICATION_TYPE_DESCRIPTIONS[1]}
                      icon={NOTIFICATION_TYPE_ICONS[1]}
                      checked={haveNotificationTypes[1]}
                      setter={setHaveNotificationTypesIndex(1)}
                    />
                    <NotificationTypeToggle
                      title={NOTIFICATION_TYPE_STRINGS[2]}
                      description={NOTIFICATION_TYPE_DESCRIPTIONS[2]}
                      icon={NOTIFICATION_TYPE_ICONS[2]}
                      checked={haveNotificationTypes[2]}
                      setter={setHaveNotificationTypesIndex(2)}
                    />
                  </div>
                  {notificationSettingsError && <p className="text-sm text-red-400 mt-4">{notificationSettingsError}</p>}
                  {notificationSettingsSuccess && <p className="text-sm text-emerald-400 mt-4">Settings Applied</p>}
                  <div className="flex justify-end mt-5">
                    <button type="submit" disabled={notificationSettingsApplying || !notificationSettingsChanged} className={saveButtonClass}>
                      {notificationSettingsApplying ? 'Applying…' : 'Apply settings'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProfilePage;
